<script setup lang="ts">
  import { Field, Picker, Popup, Uploader, Button as VButton, Form as VForm, Switch as VSwitch, showToast } from "vant";
  import type { UploaderFileListItem } from "vant";
  import { computed, onMounted, onUnmounted, reactive, ref, useTemplateRef } from "vue";
  import regexp from "~/assets/data/regexp";
  import credentialsImage from "~/assets/images/h5/dls/entry-form/credentials.svg";
  import userImage from "~/assets/images/h5/dls/entry-form/user.svg";
  import { useAliyunCaptcha } from "~/composables/global/useAliyunCaptcha";
  import { useApi } from "~/composables/useApi";
  import Common from "~/utils/common";

  const emit = defineEmits(["success"]);
  const personPictureUrlRef = useTemplateRef("personPictureUrlRef");
  const lawyerCardUrlRef = useTemplateRef("lawyerCardUrlRef");

  const $api = useApi();

  const userImageRef = ref(userImage);
  const credentialsImageRef = ref(credentialsImage);

  const areaList = ref([]);
  const goodAtDomainList = ref([]);
  const form = reactive({
    name: "",
    phone: "",
    code: "",
    email: "",
    personPictureUrl: "",
    lawyerCardUrl: "",
    lawyerCardNum: "",
    goodAtDomain: "",
    address: "",
    workUnit: "",
    desc: "",
    show: true,
  });

  interface FileData {
    personPictureUrl: UploaderFileListItem[];
    lawyerCardUrl: UploaderFileListItem[];
  }

  const fileData = reactive<FileData>({
    personPictureUrl: [],
    lawyerCardUrl: [],
  });

  const rules = {
    name: [{ required: true, message: "请填写姓名" }],
    code: [{ required: true, message: "请填写验证码" }],
    workUnit: [{ required: true, message: "请填写律所全称" }],
    goodAtDomain: [{ required: true, message: "请选择专业领域" }],
    address: [{ required: true, message: "请选择工作地区" }],
    phone: [
      { required: true, message: "请填写联系电话" },
      { pattern: regexp.phone, message: "请输入正确格式的手机号码" },
    ],
    email: [
      { required: true, message: "请填写邮箱地址" },
      { pattern: regexp.email, message: "请输入正确格式的邮箱地址" },
    ],
    lawyerCardNum: [
      { required: true, message: "请填写执业证号" },
      {
        validator: (val: string) => {
          if (val) {
            return regexp.lawyerCardNum.test(val);
          }
          return true;
        },
        message: "请输入正确格式的执业证号",
      },
    ],
  };

  const state = reactive({
    uploadLoading: false,
    submitLoading: false,
    areaVisible: false,
    goodAtDomainVisible: false,
    smsSendBtn: false,
    smsTime: 60,
    uploadMaxSize: 1024 * 1024 * 10,
    fileSuffixList: ["jpg", "jpeg", "png"],
    fileAcceptType: ".jpg,.jpeg,.png",
    domainMaxCount: 3,
  });

  const domainList = computed(() => {
    return form.goodAtDomain ? form.goodAtDomain.split("、").filter(Boolean) : [];
  });

  let observer: MutationObserver | null = null;
  const hanleTouchIssue = () => {
    observer = new MutationObserver((mutationsList) => {
      const identifyingCode = mutationsList.find((item) => (item.target as HTMLElement).id === "identifyingCode");
      if (identifyingCode) (identifyingCode.target as HTMLElement).setAttribute("data-no-touch-simulate", "true");
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const onSubmit = () => {
    const params = { ...form };
    if (!params.lawyerCardUrl) {
      showToast("请上传执业资格证");
      return;
    }
    if (state.uploadLoading) {
      showToast("请等待文件上传完成");
      return;
    }
    state.submitLoading = true;
    $api
      .lawyerRegister(params)
      .then(() => {
        emit("success");
      })
      .finally(() => {
        state.submitLoading = false;
      });
  };
  const loadCityList = async () => {
    try {
      const list = await $api.getCityList();

      // 确保数据格式正确，统一字段名为 text
      const formattedList = list.map((province: any) => {
        const formattedProvince: any = {
          text: province.label || province.text || province.name || "未知省份",
          value: province.value || province.code || province.id || "",
          children: [],
        };

        // 处理子级数据
        if (province.children && province.children.length > 0) {
          formattedProvince.children = province.children.map((city: any) => ({
            text: city.label || city.text || city.name || "未知城市",
            value: city.value || city.code || city.id || "",
          }));
        }
        // 如果没有子级，保持 children 为空数组，不添加任何选项

        return formattedProvince;
      });

      areaList.value = formattedList;
    } catch (error) {
      console.error("加载城市数据失败:", error);
      showToast("加载城市数据失败，请重试");
    }
  };

  const loadCardDomains = async () => {
    const list = await $api.getCardDomains();
    goodAtDomainList.value = list;
  };

  const onAddressConfirm = (result: any) => {
    // Vant 3 返回的是包含 selectedValues, selectedOptions, selectedIndexes 的对象
    const { selectedValues, selectedOptions } = result;

    // 处理选中的值
    let values: string[] = [];

    if (Array.isArray(selectedValues)) {
      values = selectedValues.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.text || item.label || item.value || item.name || "";
        } else {
          return String(item);
        }
      });
    }

    // 如果 selectedValues 没有有效值，尝试使用 selectedOptions
    if (values.every((v) => !v || v.trim() === "") && Array.isArray(selectedOptions)) {
      values = selectedOptions.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.text || item.label || item.value || item.name || "";
        } else {
          return String(item);
        }
      });
    }

    // 过滤空值、undefined、null 等无效值
    const filteredValues = values.filter((item) => {
      return (
        item &&
        item !== "undefined" &&
        item !== "null" &&
        item.trim() !== "" &&
        item.trim() !== "undefined" &&
        item.trim() !== "null"
      );
    });

    // 特殊处理：如果只有一个有效值（比如直辖市），直接使用
    if (filteredValues.length === 1) {
      form.address = filteredValues[0] as string;
    } else if (filteredValues.length > 1) {
      form.address = filteredValues.join("/");
    } else {
      form.address = "";
    }

    state.areaVisible = false;
  };

  // const onGoodAtDomainConfirm = (value: string) => {
  //   form.goodAtDomain = value;
  //   state.goodAtDomainVisible = false;
  // }

  const onOpenUplaod = (ref: any) => {
    ref?.chooseFile();
  };

  const formRef = ref();
  const { open } = useAliyunCaptcha();
  const getCaptcha = async () => {
    await formRef.value.validate("phone");
    open("#captcha-button", (captchaVerifyParam: string) => {
      const phone = form.phone;
      state.smsSendBtn = true;
      const interval = setInterval(() => {
        if (state.smsTime-- <= 0) {
          state.smsTime = 60;
          state.smsSendBtn = false;
          clearInterval(interval);
        }
      }, 1000);
      const params = {
        type: "register",
        phone,
        captchaVerifyParam,
      };
      $api.sendSmsCaptcha(params);
    });
  };

  const onPersonPictureUpload = async (data: any) => {
    data.status = "uploading";
    const { file } = data;
    try {
      fileCheck(file);
      state.uploadLoading = true;
      const result = await fileUpload(file);
      form.personPictureUrl = result;
      data.status = "done";
      data.url = result;
    } catch (e) {
      data.status = "failed";
      fileData.personPictureUrl = [];
      form.personPictureUrl = "";
      console.error(e);
    } finally {
      state.uploadLoading = false;
    }
  };
  //上传执业资格证
  const onLawyerCardUpload = async (data: any) => {
    data.status = "uploading";
    const { file } = data;
    try {
      fileCheck(file);
      state.uploadLoading = true;
      const result = await fileUpload(file);
      form.lawyerCardUrl = result;
      data.status = "done";
      // 更新文件数据以显示预览
      data.url = result;

      // 确保文件被添加到 fileData 数组中
      if (fileData.lawyerCardUrl.length === 0) {
        fileData.lawyerCardUrl.push(data);
      } else {
        fileData.lawyerCardUrl[0] = data;
      }
    } catch (e) {
      data.status = "failed";
      fileData.lawyerCardUrl = [];
      form.lawyerCardUrl = "";
      console.error(e);
    } finally {
      state.uploadLoading = false;
    }
  };

  //检查文件
  const fileCheck = (file: File) => {
    const maxFileSize = state.uploadMaxSize;
    const maxFileSizeM = maxFileSize / 1024 / 1024;
    const suffixList = state.fileSuffixList;
    const isLtM = file.size < maxFileSize;
    if (!isLtM) {
      showToast(`文件大小不能超过${maxFileSizeM}M`);
      throw new Error();
    }
    const suffix = Common.getFileSuffix(file.name);
    if (!suffixList.includes(suffix)) {
      const suffixStr = suffixList.join("、");
      showToast(`只能上传${suffixStr}文件`);
      throw new Error();
    }
  };

  const fileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return await $api.uploadFileGetUrl(formData);
  };

  const onDomainItemClick = (value: string) => {
    const maxCount = state.domainMaxCount;
    const list = [...domainList.value];

    if (list.includes(value)) {
      list.splice(list.indexOf(value), 1);
    } else {
      if (list.length >= maxCount) {
        showToast(`最多只能选择${maxCount}项`);
        return;
      }
      list.push(value);
    }
    form.goodAtDomain = list.join("、");
  };

  const onDomainReset = () => {
    form.goodAtDomain = "";
  };

  const onDomainConfirm = () => {
    state.goodAtDomainVisible = false;
  };

  onMounted(() => {
    loadCityList();
    loadCardDomains();
    hanleTouchIssue();
  });

  onUnmounted(() => {
    observer?.disconnect();
  });
</script>

<template>
  <div class="apply-form">
    <div class="header">得理律助 律师入驻</div>
    <div class="privilege">
      <div class="privilege-title">入驻权益</div>
      <div class="privilege-content">
        <div class="privilege-item">
          <icon-font type="Group2384" />
          <!-- <Icon name="custom:group2384" />-->
          <span>1个月免费使用律助</span>
        </div>
        <div class="privilege-item">
          <icon-font type="Group2391" />
          <!-- <Icon name="custom:group2391" />-->
          <span>AI大模型智能问答</span>
        </div>
        <div class="privilege-item">
          <icon-font type="Group2596" />
          <!-- <Icon name="custom:group2596" />-->
          <span>案源广场拓展案源</span>
        </div>
        <div class="privilege-item">
          <icon-font type="Group2392" />
          <!-- <Icon name="custom:group2392" />-->
          <span>全域法律数据智能检索</span>
        </div>
        <div class="privilege-item">
          <icon-font type="Group2368" />
          <!-- <Icon name="custom:group2368" />-->
          <span>专属律师智慧秘书</span>
        </div>
        <div class="privilege-item">
          <icon-font type="Group2393" />
          <!-- <Icon name="custom:group2393" />-->
          <span>精准掌控法律商机和资讯</span>
        </div>
      </div>
    </div>
    <div class="form">
      <VForm ref="formRef" :show-error="false" @submit="onSubmit">
        <div class="form-title">得理律助入驻申请表</div>
        <Field v-model="form.name" label="*姓名" placeholder="请输入您的姓名" :maxlength="8" :rules="rules.name" />
        <Field
          v-model="form.phone"
          name="phone"
          label="*联系电话"
          inputmode="numeric"
          placeholder="请输入您常用的手机号码"
          :maxlength="11"
          :rules="rules.phone" />
        <Field
          v-model="form.code"
          label="*验证码"
          inputmode="numeric"
          placeholder="请输入验证码"
          :maxlength="6"
          :rules="rules.code">
          <template #button>
            <VButton
              id="captcha-button"
              class="send-code-btn"
              size="small"
              type="primary"
              native-type="button"
              :disabled="state.smsSendBtn"
              @click="getCaptcha()">
              {{ state.smsSendBtn ? `${state.smsTime} s` : "发送验证码" }}
            </VButton>
          </template>
        </Field>
        <Field
          v-model="form.email"
          label="*邮箱"
          type="email"
          placeholder="请输入您的邮箱地址"
          :maxlength="32"
          :rules="rules.email" />
        <Field
          v-model="form.workUnit"
          label="*律所全称"
          placeholder="请输入律师事务所全称"
          :maxlength="30"
          :rules="rules.workUnit" />
        <Field
          v-model="form.address"
          name="address"
          label="*工作地区"
          placeholder="请选择工作地区"
          arrow-direction="down"
          readonly
          is-link
          :rules="rules.address"
          @click="state.areaVisible = true" />
        <Field
          v-model="form.goodAtDomain"
          name="goodAtDomain"
          label="*专业领域"
          placeholder="请选择专业领域"
          arrow-direction="down"
          readonly
          is-link
          :rules="rules.goodAtDomain"
          @click="state.goodAtDomainVisible = true" />
        <Field
          v-model="form.lawyerCardNum"
          label="*执业证号"
          placeholder="请输入您的律师执业证号"
          :maxlength="17"
          :rules="rules.lawyerCardNum" />
        <Field
          v-model="form.desc"
          class="textarea no-line"
          :rows="3"
          autosize
          label="个人简介"
          type="textarea"
          :maxlength="500"
          placeholder="请输入个人简介" />
        <div class="upload-wrap">
          <div class="upload-item">
            <Uploader
              ref="personPictureUrlRef"
              v-model="fileData.personPictureUrl"
              preview-size="3.5rem"
              :upload-icon="userImageRef"
              :max-count="1"
              :accept="state.fileAcceptType"
              :after-read="onPersonPictureUpload"
              @delete="form.personPictureUrl = ''" />
            <div class="upload-info">
              <a @click="onOpenUplaod(personPictureUrlRef)">点击上传头像</a>
              <p>支持jpg、png、jpeg格式</p>
            </div>
          </div>
          <div class="upload-item">
            <Uploader
              ref="lawyerCardUrlRef"
              v-model="fileData.lawyerCardUrl"
              preview-size="3.5rem"
              :upload-icon="credentialsImageRef"
              :max-count="1"
              :accept="state.fileAcceptType"
              :after-read="onLawyerCardUpload"
              @delete="form.lawyerCardUrl = ''" />
            <div class="upload-info">
              <a @click="onOpenUplaod(lawyerCardUrlRef)">点击上传执业资格证</a>
              <p>要求清晰，可以看清职业证号、姓名、身份证号</p>
            </div>
          </div>
        </div>

        <div class="show-warp">
          <div class="label">是否将我的名片推送给需要咨询的客户</div>
          <VSwitch v-model="form.show" class="switch" size="1.4375rem" inactive-color="#D9D9D9" />
        </div>

        <div class="footer">
          <VButton
            class="submit-btn"
            block
            type="primary"
            native-type="submit"
            :loading="state.submitLoading"
            loading-text="提交中...">
            提交
          </VButton>
        </div>

        <Popup v-model:show="state.goodAtDomainVisible" position="bottom" overlay-class="domain-popup-overlay">
          <div class="domain-popup">
            <div class="domain-header">
              <div class="domain-title">专业领域</div>
              <div class="domain-total"> {{ domainList.length }}/{{ state.domainMaxCount }} </div>
            </div>
            <div class="domain-items roll">
              <div
                v-for="(item, index) in goodAtDomainList"
                :key="index"
                :class="{ active: domainList.includes(item) }"
                class="domain-item"
                @click="onDomainItemClick(item)">
                {{ item }}
              </div>
            </div>
            <div class="domain-footer">
              <VButton class="reset-btn" block native-type="button" @click="onDomainReset()">重置</VButton>
              <VButton class="confirm-btn" block type="primary" native-type="button" @click="onDomainConfirm()">
                确定
              </VButton>
            </div>
          </div>
          <!-- <Picker show-toolbar title="选择专业领域" :columns="goodAtDomainList" @confirm="onGoodAtDomainConfirm"
            @cancel="state.goodAtDomainVisible = false" /> -->
        </Popup>
        <Popup v-model:show="state.areaVisible" position="bottom" overlay-class="area-popup-overlay">
          <Picker
            show-toolbar
            title="选择工作地区"
            :columns="areaList"
            :columns-field-names="{ text: 'text', value: 'value', children: 'children' }"
            @confirm="onAddressConfirm"
            @cancel="state.areaVisible = false" />
        </Popup>
      </VForm>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .apply-form {
    .header {
      padding-top: 58px;
      padding-bottom: 30px;
      font-size: 20px;
      font-weight: bold;
      color: #fff;
      text-align: center;
    }

    .privilege {
      .privilege-title {
        position: relative;
        width: calc(100% - 60px);
        margin: 0 auto;
        font-size: 16px;
        font-weight: bold;
        color: #fff;
        text-align: center;

        &::before {
          position: absolute;
          top: 50%;
          left: 0;
          width: 32.4%;
          height: 1px;
          content: "";
          background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 100%) 100%);
          transform: translateY(-50%);
        }

        &::after {
          position: absolute;
          top: 50%;
          right: 0;
          width: 32.4%;
          height: 1px;
          content: "";
          background: linear-gradient(270deg, rgba(255, 255, 255, 0%) 0%, #fff 100%);
          transform: translateY(-50%);
        }
      }

      .privilege-content {
        display: flex;
        flex-wrap: wrap;
        padding-top: 10px;
        padding-bottom: 14px;

        .privilege-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 50%;
          margin-bottom: 16px;
          font-size: 12px;
          color: #fff;

          .anticon {
            margin-bottom: 5px;
            font-size: 24px;
          }
        }
      }
    }

    .form {
      padding: 16px;
      padding-bottom: 22px;
      background: #e6ebff;
      border-radius: 32px 32px 0 0;

      :deep(.van-form) {
        overflow: hidden;
        background: #fff;
        border-radius: 22px;

        .no-line {
          &::after {
            content: none;
          }
        }

        .van-cell {
          padding-top: 24px;
          padding-bottom: 16px;
        }

        .van-field__label {
          color: #1f1f1f;
        }

        .van-field__control {
          &::placeholder {
            color: #999;
          }
        }

        .van-uploader {
          .van-uploader__preview {
            margin: 0;
          }
        }

        .van-uploader__upload {
          // width: 56px;
          // height: 56px;
          margin: 0;
          background: #f1f1f1;

          .van-uploader__upload-icon {
            .van-icon__image {
              width: 30px;
              height: 30px;
            }
          }
        }

        .textarea {
          flex-direction: column;

          .van-cell__value {
            padding-top: 16px;

            textarea {
              padding: 10px;
              background: #f5f5f5;
            }
          }
        }
      }

      .form-title {
        padding-top: 24px;
        font-size: 16px;
        font-weight: bold;
        color: #333;
        text-align: center;
      }

      .send-code-btn {
        min-width: 80px;
        height: 24px;
        border-radius: 60px;
      }

      .upload-wrap {
        padding: 14px 15px;
        padding-bottom: 0;

        .upload-item {
          display: flex;
          margin-bottom: 30px;

          .upload-info {
            padding-left: 44px;

            a {
              font-size: 14px;
              color: #3a78fe;
              text-decoration: underline;
            }

            p {
              margin: 0;
              font-size: 12px;
              color: #999;
            }
          }
        }
      }

      .show-warp {
        display: flex;
        justify-content: space-between;
        padding: 0 15px;

        .label {
          font-size: 14px;
          color: #1f1f1f;
        }
      }

      .footer {
        padding: 40px 15px 30px;

        .submit-btn {
          height: 48px;
          font-size: 16px;
          font-weight: bold;
          border-radius: 60px;
        }
      }
    }
  }

  .area-popup-overlay {
    & + .van-popup {
      right: 0;
      left: 0;
      max-width: 667px;
      margin: 0 auto;
    }
  }

  .domain-popup-overlay {
    & + .van-popup {
      right: 0;
      left: 0;
      max-width: 667px;
      margin: 0 auto;
      border-radius: 12px 12px 0 0;

      .domain-popup {
        .domain-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 16px 16px;

          .domain-title {
            font-size: 16px;
            font-weight: 600;
            color: #1d2129;
          }

          .domain-total {
            font-size: 14px;
            color: #4e5969;
          }
        }

        .domain-items {
          display: grid;
          grid-template-rows: repeat(3, 1fr);
          grid-template-columns: repeat(3, 1fr);
          grid-gap: 14px 12px;
          // height: 182px;
          height: 27vh;
          padding: 0 16px;
          overflow-y: auto;

          .domain-item {
            display: inline-block;
            height: 42px;
            padding: 10px 4px;
            overflow: hidden;
            font-size: 15px;
            color: #1d2129;
            text-align: center;
            text-overflow: ellipsis;
            white-space: nowrap;
            cursor: pointer;
            background: #f6f6f6;
            border: #f6f6f6 solid 1px;
            border-radius: 4px;
            transition: ease 300ms all;

            &.active {
              color: #2f78ff;
              background: #e9f3fd;
              border-color: #2f78ff;
            }

            &.custom {
              padding-top: 0;
              padding-bottom: 0;
              background: none;
              border-color: #dadadc;
              border-style: dashed;

              input {
                width: 100%;
                height: 100%;
                text-align: center;
                border: none;
                box-shadow: none;

                &::placeholder {
                  color: #dadadc;
                }
              }
            }
          }
        }

        .domain-footer {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-column-gap: 15px;
          padding: 16px;

          button {
            border-radius: 8px;
          }

          .reset-btn {
            background: #f6f6f6;
            border-color: #f6f6f6;
          }

          .confirm-btn {
            background: #2f78ff;
            border-color: #2f78ff;
          }
        }
      }
    }
  }
</style>
